<template>
  <v-container fluid>
    <v-row>
      <v-col cols='12'>
        <div class='d-flex align-center mb-2'>
          <span class='title'>Invoice PDF Designer</span>
        </div>

        <v-card outlined class='mb-4'>
          <div class='toolbar pa-2 d-flex align-center flex-wrap'>
            <menu-bar v-if='editor' class='d-flex align-center flex-wrap' :editor='editor' />
            <v-divider vertical class='mx-2' />
            <v-menu offset-y>
              <template v-slot:activator='{ on }'>
                <v-btn small text v-on='on'>
                  <v-icon left small>mdi-tag-plus</v-icon>
                  Insert Field
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item v-for='field in availableFields' :key='field' @click='insertField(field)'>
                  <v-list-item-title>{{ field }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
          <v-divider />
          <editor-content v-if='editor' class='editor-content pa-4' :editor='editor' />
        </v-card>

        <v-card outlined class='mb-4 pa-4'>
          <div class='subtitle-1 mb-2'>Preview against a real invoice</div>
          <v-row align='center'>
            <v-col cols='6' md='4'>
              <v-text-field
                v-model='invoiceSearch'
                label='Search invoice number'
                dense
                hide-details
                :loading='searching'
                @input='searchInvoices'
              />
              <v-list v-if='invoiceResults.length' dense>
                <v-list-item
                  v-for='r in invoiceResults'
                  :key='r.id'
                  @click='selectInvoice(r)'
                >
                  <v-list-item-title>{{ r.number }} — {{ r.bill_to_display }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols='6' md='4' v-if='selectedInvoice'>
              Selected invoice: <strong>{{ selectedInvoice.number }}</strong>
            </v-col>
            <v-col cols='12' md='4'>
              <v-btn
                color='primary'
                :disabled='!selectedInvoice'
                :loading='previewLoading'
                @click='preview'
              >
                Preview
              </v-btn>
            </v-col>
          </v-row>

          <iframe
            v-if='previewHtml'
            class='preview-frame mt-4'
            :srcdoc='previewHtml'
          />
        </v-card>

        <v-card outlined class='mb-4 pa-4'>
          <v-row align='center'>
            <v-col cols='12' md='6'>
              <v-text-field
                v-model='label'
                label='Version label (optional)'
                dense
                hide-details
              />
            </v-col>
            <v-col cols='12' md='6' class='text-right'>
              <v-btn class='mr-2' :loading='saving' @click='saveVersion'>Save as version</v-btn>
              <v-btn
                color='success'
                :disabled='!currentTemplateId || !hasPreviewed'
                :loading='activating'
                @click='activate(currentTemplateId)'
              >
                Activate
              </v-btn>
            </v-col>
          </v-row>
          <div v-if='!hasPreviewed' class='caption grey--text mt-2'>
            Preview this design against a real invoice before it can be activated.
          </div>
        </v-card>

        <v-card outlined class='pa-4'>
          <div class='subtitle-1 mb-2'>Version history</div>
          <v-list dense>
            <v-list-item v-for='t in templates' :key='t.id'>
              <v-list-item-content>
                <v-list-item-title>
                  {{ t.label || ('Version ' + t.id) }}
                  <v-chip v-if='t.is_active' x-small color='success' class='ml-2'>Active</v-chip>
                </v-list-item-title>
                <v-list-item-subtitle>{{ t.created_at | formatDate }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn v-if='!t.is_active' small text @click='activate(t.id)'>Activate</v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { EditorContent, Editor } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import MenuBar from '@/modules/notices/components/MenuBar';
import accountingAPI from '@/api/accounting';
import moment from 'moment';
import _ from 'lodash';

const DEFAULT_TEMPLATE_HTML = `
  <h1>Invoice [Invoice Number]</h1>
  <p><strong>[Division Name]</strong></p>
  <p>Bill To: [Bill To Name]</p>
  <p>Invoice Date: [Invoice Date] &nbsp; Terms: [Terms]</p>
  [Line Items Table]
  <p>Subtotal: [Subtotal] &nbsp; Tax: [Tax] &nbsp; Total: [Total]</p>
  [Payments Table]
  <p>Amount Outstanding: [Amount Outstanding]</p>
  <p>[Resolution]</p>
`;

export default {
  name: 'invoice-template-designer',
  data: () => ({
    editor: null,
    availableFields: [
      '[Invoice Number]',
      '[Invoice Date]',
      '[Terms]',
      '[Subtotal]',
      '[Tax]',
      '[Total]',
      '[Amount Outstanding]',
      '[Bill To Name]',
      '[Division Name]',
      '[Resolution]',
      '[Line Items Table]',
      '[Payments Table]'
    ],
    templates: [],
    label: '',
    saving: false,
    activating: false,
    currentTemplateId: null,
    hasPreviewed: false,
    invoiceSearch: '',
    invoiceResults: [],
    searching: false,
    selectedInvoice: null,
    previewHtml: '',
    previewLoading: false
  }),
  filters: {
    formatDate (val) {
      return val ? moment(val).format('MMM D, YYYY h:mm A') : '';
    }
  },
  async mounted () {
    this.templates = await accountingAPI.getInvoiceTemplates() || [];
    const active = this.templates.find(t => t.is_active);

    this.editor = new Editor({
      content: active ? active.html : DEFAULT_TEMPLATE_HTML,
      extensions: [
        StarterKit,
        Underline,
        TextAlign.configure({ types: ['heading', 'paragraph'] })
      ]
    });
  },
  beforeDestroy () {
    if (this.editor) {
      this.editor.destroy();
    }
  },
  methods: {
    insertField (field) {
      this.editor.chain().focus().insertContent(field + ' ').run();
      this.hasPreviewed = false;
    },
    searchInvoices: _.debounce(async function () {
      const term = this.invoiceSearch.trim();
      if (term.length < 3) {
        this.invoiceResults = [];
        return;
      }
      this.searching = true;
      this.invoiceResults = await accountingAPI.invoiceLookup(term, null, false) || [];
      this.searching = false;
    }, 300),
    selectInvoice (invoice) {
      this.selectedInvoice = invoice;
      this.invoiceResults = [];
      this.invoiceSearch = invoice.number;
    },
    async preview () {
      if (!this.selectedInvoice || !this.editor) return;
      this.previewLoading = true;
      this.previewHtml = await accountingAPI.previewInvoiceTemplate(
        this.selectedInvoice.id,
        this.editor.getHTML()
      );
      this.hasPreviewed = true;
      this.previewLoading = false;
    },
    async saveVersion () {
      if (!this.editor) return;
      this.saving = true;
      const saved = await accountingAPI.createInvoiceTemplate({
        html: this.editor.getHTML(),
        label: this.label
      });
      if (saved) {
        this.currentTemplateId = saved.id;
        this.templates = await accountingAPI.getInvoiceTemplates() || [];
      }
      this.saving = false;
    },
    async activate (id) {
      if (!id) return;
      this.activating = true;
      await accountingAPI.activateInvoiceTemplate(id);
      this.templates = await accountingAPI.getInvoiceTemplates() || [];
      this.activating = false;
    }
  },
  components: {
    'editor-content': EditorContent,
    'menu-bar': MenuBar
  }
}
</script>

<style scoped>
.editor-content {
  min-height: 400px;
}

.preview-frame {
  width: 100%;
  height: 800px;
  border: 1px solid #ccc;
}
</style>
